/**
 * Реєстр унікальних номерів ВМД (Підпорція 3.2b, 2026-05-25).
 *
 * View-backed read-only сторінка з aggregates через JOIN:
 *  - receipts_count (приходів через customs_declarations_items.goods_receipt_item_id)
 *  - tax_invoices_count (ПН з customs_declaration_id)
 *  - items_count (рядків CDI)
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  customsNumbersRegister,
  type CustomsRegisterEntry,
  type DeclarationType,
} from '@/api/taxDeclarationArticles';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function CustomsNumbersRegisterPage() {
  const [declType, setDeclType] = useState<DeclarationType | ''>('');
  const list = useQuery({
    queryKey: ['customs-numbers-register', declType],
    queryFn: () => customsNumbersRegister.list({
      declaration_type: declType || undefined,
      limit: 500,
    }),
  });
  const cols = useMemo<ColumnDef<CustomsRegisterEntry>[]>(
    () => [
      { key: 'customs_number', header: '№ ВМД', width: 200, sortValue: (r) => r.customs_number ?? '', cell: (r) => r.customs_number },
      { key: 'customs_date', header: 'Дата ВМД', width: 130, sortValue: (r) => r.customs_date ?? '', cell: (r) => r.customs_date },
      { key: 'declaration_type', header: 'Тип', width: 110, sortValue: (r) => r.declaration_type ?? '', cell: (r) => r.declaration_type },
      { key: 'country_origin_code', header: 'Країна', width: 100, sortValue: (r) => r.country_origin_code ?? '', cell: (r) => r.country_origin_code },
      { key: 'customs_office_name', header: 'Митниця', sortValue: (r) => r.customs_office_name ?? '', cell: (r) => r.customs_office_name },
      { key: 'total_customs_value', header: 'Митна варт.', width: 150, align: 'right', sortValue: (r) => Number(r.total_customs_value), cell: (r) => fmt2(r.total_customs_value) },
      { key: 'total_duty', header: 'Мито', width: 130, align: 'right', sortValue: (r) => Number(r.total_duty), cell: (r) => fmt2(r.total_duty) },
      { key: 'total_vat', header: 'ПДВ', width: 130, align: 'right', sortValue: (r) => Number(r.total_vat), cell: (r) => fmt2(r.total_vat) },
      { key: 'receipts_count', header: 'Приходів', width: 110, align: 'right', sortValue: (r) => r.receipts_count, cell: (r) => r.receipts_count },
      { key: 'tax_invoices_count', header: 'ПН', width: 80, align: 'right', sortValue: (r) => r.tax_invoices_count, cell: (r) => r.tax_invoices_count },
      { key: 'items_count', header: 'Рядків', width: 90, align: 'right', sortValue: (r) => r.items_count, cell: (r) => r.items_count },
      { key: 'posted', header: 'Проведено', width: 110, align: 'center', sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ так' : 'ні' },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar" style={{ gap: 12 }}>
        <h2 style={{ margin: 0 }}>Реєстр номерів ВМД</h2>
        <select
          value={declType}
          onChange={(e) => setDeclType(e.target.value as DeclarationType | '')}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Усі типи</option>
          <option value="IMPORT">IMPORT</option>
          <option value="EXPORT">EXPORT</option>
        </select>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<CustomsRegisterEntry>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="customs_date"
        initialSortDir="desc"
      />
    </>
  );
}
